import { Inject, Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable } from 'rxjs';

import { MESSAGING_ENV } from '../config/micros-env.tokens';
import { EnvMicroMessaging } from '../config/env.micro.messaging';
import { SessionCtl } from './session.ctl.service';
import { MessagingSdk } from 'lib-klab-sdks';

const { CHAT_EVENTS } = MessagingSdk;
const ACK_TIMEOUT_MS = 10_000;

/**
 * Thin RxJS wrapper around the socket.io-client connection to the KlabChat
 * messaging gateway.
 *
 * <p>Responsibilities:
 * <ul>
 *   <li>Build the connection URL from {@link EnvMicroMessaging} and inject the
 *       JWT in the handshake `auth.token` field.</li>
 *   <li>Expose a {@link connected$} stream of connection state.</li>
 *   <li>Expose typed RxJS observables for each server-pushed event.</li>
 *   <li>Provide {@link emit} and {@link emitWithAck} helpers for client-emitted events.</li>
 * </ul>
 *
 * <p>This service does NOT implement the {@code MessagingPort} interface — that
 * concern lives in {@code MessagingPortAdapter}. This is the lowest-level
 * transport, intended to be reused by the adapter without leaking socket.io
 * specifics to the rest of the application.
 */
@Injectable({ providedIn: 'root' })
export class MessagingSocketService implements OnDestroy {
  private socket?: Socket;
  private intentionalDisconnect = false;
  private manualReconnectInFlight = false;

  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  /** Stream of the current connection state. */
  readonly connected$ = this.connectedSubject.asObservable();

  constructor(
    @Inject(MESSAGING_ENV) private readonly env: EnvMicroMessaging,
    private readonly sessionCtl: SessionCtl,
  ) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Opens the socket and emits on {@link connected$} once the handshake completes.
   *
   * <p>If the socket is already connected, this is a no-op.
   *
   * @returns Observable that completes after a successful {@code connect} event,
   *          or errors on connection failure / auth rejection.
   */
  connect(): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.intentionalDisconnect = false;

      if (this.socket?.connected) {
        this.manualReconnectInFlight = false;
        subscriber.next();
        subscriber.complete();
        return undefined;
      }

      if (this.socket) {
        const socket = this.socket;
        const onConnect = () => {
          this.manualReconnectInFlight = false;
          socket.off('connect_error', onError);
          subscriber.next();
          subscriber.complete();
        };
        const onError = (err: Error) => {
          if (!socket.active) this.manualReconnectInFlight = false;
          socket.off('connect', onConnect);
          subscriber.error(err);
        };
        socket.once('connect', onConnect);
        socket.once('connect_error', onError);
        if (!socket.active && !this.manualReconnectInFlight) {
          this.manualReconnectInFlight = true;
          socket.connect();
        }
        return () => {
          socket.off('connect', onConnect);
          socket.off('connect_error', onError);
        };
      }

      const token = this.sessionCtl.getAccessTokenOrThrow();

      // Socket.IO separates the namespace from the Engine.IO path:
      // - The URL carries the namespace handled by NestJS: /v1/chat.
      // - The `path` option carries the Engine.IO endpoint routed by the Gateway.
      //
      // Gateway mode:
      //   URL:  https://gatewayapi-dev.klab.inclub.world/v1/chat
      //   path: /ms-messaging-ws/socket.io
      //   HTTP handshake: /ms-messaging-ws/socket.io/?EIO=4&transport=websocket
      //
      // Direct WS mode keeps the default Engine.IO path:
      //   URL:  http://localhost:3000/v1/chat
      //   path: /socket.io
      const url = `${this.env.host}${this.env.wsNamespace}`;
      const socketPath = this.engineIoPath();
      const userId = this.sessionCtl.getUserId();
      const employeeId = this.sessionCtl.getEmployeeId();
      const bearerToken = `Bearer ${token}`;

      // Token is sent BOTH as:
      //   1) auth payload — available in browser Socket.IO clients.
      //   2) extraHeaders — preserved for Node.js/test clients that can send headers.
      // MOMSG-1159: socket.io-client's own reconnection backoff caps at
      // reconnectionDelayMax (5 min, see below) — after a real outage (wifi
      // drop, laptop sleep) the client can sit idle for minutes before its
      // next scheduled attempt even though the network is already back. This
      // listener forces an immediate attempt the moment the OS reports the
      // network is up, instead of waiting out the backoff.
      window.removeEventListener('online', this.onNetworkOnline);
      window.addEventListener('online', this.onNetworkOnline);

      this.socket = io(url, {
        path: socketPath,
        transports: ['websocket'],
        // Function form (not a static object): socket.io-client invokes this on
        // EVERY (re)connection attempt, including the infinite auto-reconnects
        // below. A static object would freeze the token captured at construction
        // time, so a silent token refresh elsewhere in the app (which revokes the
        // old token server-side) would leave the socket retrying forever with a
        // dead token while the rest of the app keeps working fine (MOMSG-931).
        auth: (cb) =>
          cb({
            token: `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`,
            userId: this.sessionCtl.getUserId(),
            employeeId: this.sessionCtl.getEmployeeId(),
          }),
        extraHeaders: {
          Authorization: bearerToken,
          ...(userId !== undefined ? { 'X-User-Id': String(userId) } : {}),
          ...(employeeId !== undefined ? { 'X-Employee-Id': String(employeeId) } : {}),
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        // 5 minutes, not 5 seconds: socket.io-client backs off exponentially
        // between reconnectionDelay and this ceiling on every failed attempt.
        // A low ceiling meant a client stuck on a dead/revoked token (see the
        // auth-callback comment above) hammered the gateway and IAM every
        // 1-5s forever. A high ceiling still self-heals automatically the
        // moment the underlying session becomes valid again — it just stops
        // being a self-inflicted reconnect storm while it isn't (MOMSG-931).
        reconnectionDelayMax: 300_000,
      });
      const socket = this.socket;

      socket.on('connect', () => {
        this.manualReconnectInFlight = false;
        this.connectedSubject.next(true);
        // Register beforeunload so closing the tab/window emits client:logout
        // before the transport tears down — gives the backend a chance to
        // transition this user offline immediately.
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.addEventListener('beforeunload', this.onBeforeUnload);
        subscriber.next();
        subscriber.complete();
      });

      socket.on('disconnect', (reason: string) => {
        this.connectedSubject.next(false);
        console.warn('[MessagingSocket] disconnect', {
          reason,
          url,
          path: socketPath,
        });
        if (
          reason === 'io server disconnect' &&
          !this.intentionalDisconnect &&
          this.socket === socket &&
          !socket.active &&
          !this.manualReconnectInFlight
        ) {
          this.manualReconnectInFlight = true;
          socket.connect();
        }
      });

      socket.on('connect_error', (err: Error) => {
        if (!socket.active) this.manualReconnectInFlight = false;
        this.connectedSubject.next(false);
        console.error('[MessagingSocket] connect_error', {
          message: err.message,
          url,
          path: socketPath,
          namespace: this.env.wsNamespace,
        });
        subscriber.error(err);
      });
      return undefined;
    });
  }

  private onBeforeUnload = (): void => {
    // Best-effort: emit logout before the page unloads so the backend
    // transitions this user offline immediately. The transport might already
    // be closing, but the event has a chance to be flushed over WebSocket.
    try {
      this.socket?.emit('client:logout');
    } catch {
      // Ignore — socket is already closing.
    }
  };

  private onNetworkOnline = (): void => {
    if (!this.socket || this.socket.connected || this.intentionalDisconnect) return;
    // MOMSG-1159: `socket.active` stays true for the entire duration of
    // socket.io-client's own auto-reconnect backoff (it only flips to false
    // after an explicit disconnect() or a server-initiated 'io server
    // disconnect') — so a `!socket.active` guard, and a plain `socket.connect()`
    // call, are BOTH no-ops while a reconnect attempt is already scheduled:
    // `Socket.connect()` itself skips opening a new transport whenever the
    // Manager's private `_reconnecting` flag is true, which is exactly the
    // state this handler exists to interrupt. There is no public API to
    // cancel an in-flight backoff wait, so this resets the Manager's private
    // backoff/reconnecting state directly and reopens immediately. Wrapped
    // defensively: if socket.io-client ever renames these internals, this
    // silently no-ops and the client just falls back to waiting out its
    // normal backoff, same as before this fix existed.
    const manager = this.socket.io as unknown as {
      _reconnecting?: boolean;
      backoff?: { reset: () => void };
      open: (fn?: (err?: Error) => void) => void;
    };
    try {
      manager.backoff?.reset();
      manager._reconnecting = false;
      manager.open();
    } catch (err) {
      console.warn('[MessagingSocket] forced reconnect on network online failed', err);
    }
  };

  /** Closes the socket. Safe to call multiple times. */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this.manualReconnectInFlight = false;
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('online', this.onNetworkOnline);
    if (this.socket) {
      // Signal intentional logout BEFORE closing so the backend transitions
      // offline immediately (no 25s grace) and other users see the update
      // in real time without a page reload.
      //
      // Use emitWithAck: the server calls the ack callback confirming it
      // received the event. We wait up to 200ms for the ack, then disconnect
      // regardless — this covers both the ack arrived and the timeout cases.
      let closed = false;
      const closeSocket = (): void => {
        if (closed || !this.socket) return;
        closed = true;
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = undefined;
        this.connectedSubject.next(false);
      };

      try {
        this.socket.emit('client:logout', closeSocket);
      } catch {
        // If emit itself throws, the socket is already closing.
        closeSocket();
        return;
      }
      // Fallback: if the ack never arrives (server down, transport already
      // closing), disconnect after a short delay so the user isn't stuck.
      setTimeout(closeSocket, 200);
    }
  }

  /** Returns {@code true} if the socket is open and authenticated. */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // ── Emit helpers ───────────────────────────────────────────────────────────

  /**
   * Emits an event without expecting a callback response.
   *
   * @throws if the socket is not connected.
   */
  emit(event: string, payload: unknown): void {
    this.requireSocket().emit(event, payload);
  }

  /**
   * Emits an event and resolves with the server's callback response.
   *
   * @returns Observable that emits the callback payload exactly once.
   * @throws if the socket is not connected.
   */
  emitWithAck<TResponse>(event: string, payload: unknown): Observable<TResponse> {
    return new Observable<TResponse>((subscriber) => {
      this.requireSocket()
        .timeout(ACK_TIMEOUT_MS)
        .emit(event, payload, (err: Error | null, response: TResponse) => {
          if (err) {
            subscriber.error(
              new Error(`Socket ACK timeout for "${event}" after ${ACK_TIMEOUT_MS}ms`, {
                cause: err,
              }),
            );
            return;
          }
          subscriber.next(response);
          subscriber.complete();
        });
    });
  }

  // ── Server-pushed event streams ────────────────────────────────────────────

  /**
   * Returns a stream of server-emitted events of type {@code T} for the given event name.
   *
   * <p>The returned observable is multicast-safe — each subscription gets its own listener.
   * Make sure to unsubscribe to remove the listener; otherwise the listener leaks.
   */
  on<T>(event: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      const handler = (payload: T) => subscriber.next(payload);

      const sock = this.socketOrNull();
      if (!sock) {
        subscriber.error(new Error('Socket is not connected. Call connect() first.'));
        return;
      }

      sock.on(event, handler);

      return () => {
        sock.off(event, handler);
      };
    });
  }

  // ── Convenience strongly-typed accessors for the most common events ───────

  /** Stream of {@code chat:message:new} broadcasts. */
  onMessageNew<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.MESSAGE_NEW);
  }

  /** Stream of {@code chat:conversation:created} (creator only). */
  onConversationCreated<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.CONVERSATION_CREATED);
  }

  /** Stream of {@code chat:conversation:new} (when added to a conversation). */
  onConversationNew<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.CONVERSATION_NEW);
  }

  /** Stream of {@code chat:message:delivered:update}. */
  onMessageDeliveredUpdate<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.MESSAGE_DELIVERED_UPDATE);
  }

  /** Stream of {@code chat:message:read:update}. */
  onMessageReadUpdate<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.MESSAGE_READ_UPDATE);
  }

  /** Stream of {@code chat:presence:update}. */
  onPresenceUpdate<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.PRESENCE_UPDATE);
  }

  /** Stream of {@code chat:typing:indicator}. */
  onTypingIndicator<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.TYPING_INDICATOR);
  }

  /** Stream of {@code chat:error}. */
  onError<T>(): Observable<T> {
    return this.on<T>(CHAT_EVENTS.ERROR);
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  private requireSocket(): Socket {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket is not connected. Call connect() first.');
    }
    return this.socket;
  }

  private socketOrNull(): Socket | undefined {
    return this.socket && this.socket.connected ? this.socket : undefined;
  }

  private engineIoPath(): string {
    const basePath = this.env.apiBasePath?.trim();
    return basePath ? `${basePath.replace(/\/$/, '')}/socket.io` : '/socket.io';
  }
}
